import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  SxProps,
  Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

interface CommonModalProps {
  open: boolean;
  onClose: () => void;
  title: string | React.ReactNode;
  children: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  fullWidth?: boolean;

  // Standard form submit — wraps content in <form> and submit button becomes type="submit"
  onSubmit?: (e: React.FormEvent) => void;
  submitLabel?: string;
  cancelLabel?: string;
  submitDisabled?: boolean;
  loading?: boolean;

  // Makes submit button red (for delete/destructive confirmations) — cancel becomes grey
  danger?: boolean;

  // Completely replaces the default cancel/submit row with custom content (e.g. stepper Prev/Next)
  customActions?: React.ReactNode;

  // Pass true to hide the entire DialogActions bar (e.g. read-only display modals)
  hideActions?: boolean;

  disableEscapeKeyDown?: boolean;
  keepMounted?: boolean;
  fullScreen?: boolean;

  // Advanced: pass custom Paper sizing (e.g. fixed height dialogs like marksheet viewer)
  PaperProps?: React.ComponentProps<typeof Dialog>['PaperProps'];
  // Advanced: override DialogContent sx (replaces default paddingTop — use for p:0 layouts)
  contentSx?: SxProps<Theme>;
}

const CANCEL_GREY_SX = {
  minWidth: 100,
  borderColor: "#3092e3",
  color: "#3092e3",
  "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" },
};

const SUBMIT_DANGER_SX = {
  minWidth: 100,
  ml: 2,
  "&.Mui-disabled": { opacity: 0.6 },
};

const CANCEL_BTN_SX = {
  minWidth: 100,
  borderColor: "#3092e3",
  color: "#3092e3",
  "&:hover": {
    borderColor: "#1a6fb5",
    color: "#1a6fb5",
    backgroundColor: "rgba(48,146,227,0.08)",
  },
};

const SUBMIT_BTN_SX = {
  minWidth: 100,
  ml: 2,
  backgroundColor: "#3092e3",
  "&:hover": { backgroundColor: "primary.main" },
  "&.Mui-disabled": { backgroundColor: "#b2ebff", opacity: 0.6 },
};

const CommonModal: React.FC<CommonModalProps> = ({
  open,
  onClose,
  title,
  children,
  maxWidth = "sm",
  fullWidth = true,
  onSubmit,
  submitLabel,
  cancelLabel,
  submitDisabled = false,
  loading = false,
  danger = false,
  customActions,
  hideActions = false,
  disableEscapeKeyDown = false,
  keepMounted = false,
  fullScreen = false,
  PaperProps,
  contentSx,
}) => {
  const { t } = useTranslation();

  const dialogContent = (
    <>
      <DialogContent sx={contentSx ?? { paddingTop: '10px !important' }}>{children}</DialogContent>

      {!hideActions && (
        <DialogActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
          {customActions ?? (
            <>
              <Button
                type="button"
                variant="outlined"
                onClick={onClose}
                sx={danger ? CANCEL_GREY_SX : CANCEL_BTN_SX}
              >
                {cancelLabel ?? t("cancel")}
              </Button>
              <Button
                type={onSubmit ? "submit" : "button"}
                variant="contained"
                color={danger ? "error" : "primary"}
                disabled={submitDisabled || loading}
                sx={danger ? SUBMIT_DANGER_SX : SUBMIT_BTN_SX}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  submitLabel ?? t("submit")
                )}
              </Button>
            </>
          )}
        </DialogActions>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={disableEscapeKeyDown}
      keepMounted={keepMounted}
      fullScreen={fullScreen}
      PaperProps={PaperProps}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 1,
          backgroundColor: "#3092e3",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 49,
        }}
      >
        <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600, fontSize: "1rem", pl:1 }}>
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "#fff",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {onSubmit ? (
        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            onSubmit(e);
          }}
          sx={{ display: "flex", flexDirection: "column", flex: "1 1 auto", overflow: "hidden" }}
        >
          {dialogContent}
        </Box>
      ) : (
        dialogContent
      )}
    </Dialog>
  );
};

export default CommonModal;
